import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Menu,
  Search,
  Bell,
  User as UserIcon,
  LogOut,
  Settings,
  Sun,
  Moon,
  Check,
} from "lucide-react";
import useAppLevelAuth from "../hooks/useAppLevelAuth";
import { useHistory } from "react-router-dom";
// import { createPageUrl } from "@/utils";
// import { User } from "@/entities/User";

// MUI
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Avatar from "@mui/material/Avatar";
import Popover from "@mui/material/Popover";
import Button from "@mui/material/Button";
import Badge from "@mui/material/Badge";
import MenuMui from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Link } from "react-router-dom";

// Additional imports for Header.js elements
import jwtDecode from "jwt-decode";
import { errorAlert } from "../Components/Alerts";
import authHeader from "../Auth/authHeader";
import { ApiLink } from "../APILink/ApiLink";
import axios from "axios";
import { format } from "date-fns";
import LicenseBanner from "../Components/LicenseBanner";

import "./NewHeader.scss";

export default function NewHeader({
  onToggleSidebar,
  searchTerm = "",
  onSearchChange,
  themeMode = "light",
  onSetTheme,
  showThemeSwitcher = true,
}) {
  const { logout } = useAppLevelAuth();
  const history = useHistory();

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  // Ref for profile wrapper to detect outside clicks
  const profileRef = useRef(null);
  const searchInputRef = useRef(null);

  // Additional state from Header.js
  const [email, setEmail] = useState("");
  const headers = authHeader();
  const [userNotification, setUserNotification] = useState([]);
  const [logo, setLogo] = useState("");
  const [companyName, setCompanyName] = useState("");

  // Forms menu state
  const [forms, setForms] = useState(null);
  const openForms = Boolean(forms);
  const [themeMenuAnchor, setThemeMenuAnchor] = useState(null);

  // Notifications popover state
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [notifList, setNotifList] = useState(userNotification);

  const getLogoSrc = (logoValue) => {
    const fallback = ApiLink.PHOTO_URL + "companyLogo.png";
    if (!logoValue) return fallback;

    const normalized = String(logoValue).trim();
    if (!normalized) return fallback;

    if (/^https?:\/\//i.test(normalized)) {
      return normalized;
    }

    return ApiLink.PHOTO_URL + normalized;
  };

  useEffect(() => {
    // From Header.js
    const token = localStorage.getItem("token");
    if (token) {
      const decodedToken = jwtDecode(token);
      setEmail(decodedToken.email);
      // setRole(decodedToken.role);
    }

    fetchCompanyInfo();
    fetchUserNotification();
  }, []);

  // Handle click outside to close profile dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    if (isProfileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileOpen]);

  const handleLogout = async () => {
    await logout();
    history.push("/");
  };

  // Functions from Header.js
  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(ApiLink.API_URL + "Companies/getLogo", {
        headers,
      });

      const companyInfo = Array.isArray(response?.data)
        ? response.data[0]
        : response?.data;

      setLogo(companyInfo?.CompanyLogo || "");
      setCompanyName(companyInfo?.CompanyName || "");
    } catch (error) {
      errorAlert("Gabim në kërkesë:", error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.errorMessage
      ) {
        errorAlert(
          `Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`
        );
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
      }
    }
  };

  const fetchUserNotification = async () => {
    try {
      const response = await axios.get(
        ApiLink.API_URL + "Notification/notificationByID",
        { headers }
      );
      setUserNotification(response.data);
      setNotifList(response.data);
    } catch (error) {
      errorAlert("Gabim në kërkesë:", error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.errorMessage
      ) {
        errorAlert(
          `Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`
        );
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
      }
    }
  };

  const updateStatusNtfc = async (id) => {
    try {
      await axios.put(
        ApiLink.API_URL + `Notification/statusNotification/${id}`,
        null,
        { headers }
      );
    } catch (e) {
      // errorAlert("Nuk është modifikuar", e.response);
    }
  };

  const navigateToList = async (id) => {
    closeNotifPopover();
    await updateStatusNtfc(id);
    await fetchUserNotification();
    history.push("/configuration/notification");
  };

  const setNotificationAsRead = (id) => {
    setNotifList((prev) =>
      prev.map((n) =>
        (n.NotificationStatusID ?? n.id) === id
          ? { ...n, IsRead: true, isRead: true }
          : n
      )
    );
  };

  const unreadNotificationCount = useMemo(
    () => (notifList || []).filter((notification) => !(notification.IsRead ?? notification.isRead ?? false)).length,
    [notifList]
  );

  // Handlers for forms menu
  const handleClickForms = (event) => {
    setForms(event.currentTarget);
  };
  const handleCloseForms = () => {
    setForms(null);
  };

  // Handlers for notifications
  const handleNotifClick = (event) => {
    setNotifAnchor(event.currentTarget);
  };
  const closeNotifPopover = () => {
    setNotifAnchor(null);
  };
  const notifPopoverOpen = Boolean(notifAnchor);
  const openThemeMenu = Boolean(themeMenuAnchor);

  const themeOptions = [
    {
      value: "light",
      label: "Light",
      description: "Tema klasike e bardhë",
      icon: Sun,
    },
    {
      value: "dark-green",
      label: "Dark Green",
      description: "Dark me nuancat aktuale green",
      icon: Moon,
    },
    {
      value: "dark-black",
      label: "Black Dark",
      description: "Sfond i zi me cards dark green",
      icon: Moon,
    },
  ];

  const handleSearchIconClick = () => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
      searchInputRef.current.select();
    }
  };

  const hasSearchValue = searchTerm.trim().length > 0;

  const handleClearSearch = () => {
    if (onSearchChange) {
      onSearchChange("");
    }
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleOpenThemeMenu = (event) => {
    setThemeMenuAnchor(event.currentTarget);
  };

  const handleCloseThemeMenu = () => {
    setThemeMenuAnchor(null);
  };

  const handleSelectTheme = (nextTheme) => {
    if (onSetTheme) {
      onSetTheme(nextTheme);
    }
    handleCloseThemeMenu();
  };

  return (
    <header className="header">
      <LicenseBanner />
      <div className="header-left">
        <IconButton
          onClick={onToggleSidebar}
          className="icon-btn menu-toggle-btn"
        >
          <Menu className="icon-20" />
        </IconButton>

        <div className={`search-box ${hasSearchValue ? "has-value" : ""}`}>
          <button
            type="button"
            className="search-trigger"
            onClick={handleSearchIconClick}
            aria-label="Hap kërkimin"
          >
            <Search className="search-icon icon-16" />
          </button>
          <input
            ref={searchInputRef}
            type="text"
            id="sidebar-menu-search"
            name="sidebar-menu-search"
            autoComplete="off"
            placeholder="Kërko menu/nënmenu..."
            aria-label="Kërko menu"
            value={searchTerm}
            onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
          />
          {hasSearchValue && (
            <button
              type="button"
              className="search-clear-btn"
              onClick={handleClearSearch}
              aria-label="Pastro kërkimin"
            >
              <span className="search-clear-glyph">X</span>
            </button>
          )}
        </div>
      </div>

      <div className="header-right">
        {showThemeSwitcher && (
          <>
            <div className="header-action-slot">
              <IconButton
                onClick={handleOpenThemeMenu}
                className="icon-btn header-action-btn theme-toggle-btn"
                aria-label="Ndrysho temën"
                title="Ndrysho temën"
              >
                {themeMode === "light" ? (
                  <Sun className="icon-20" />
                ) : themeMode === "dark-black" ? (
                  <Moon className="icon-20" />
                ) : (
                  <Moon className="icon-20" />
                )}
              </IconButton>
            </div>

            <MenuMui
              anchorEl={themeMenuAnchor}
              id="theme-menu"
              open={openThemeMenu}
              onClose={handleCloseThemeMenu}
              PaperProps={{
                elevation: 0,
                className: "theme-menu-paper",
                sx: {
                  mt: 1,
                  borderRadius: "14px",
                  overflow: "hidden",
                  border: "1px solid var(--header-dropdown-border)",
                  boxShadow: "0 10px 28px rgba(0,0,0,0.24)",
                  backgroundColor: "var(--header-dropdown-bg)",
                  color: "var(--header-dropdown-text)",
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <div className="theme-menu-shell">
                <div className="theme-menu-head">
                  <p className="theme-menu-head__title">Zgjedh Temën</p>
                  <p className="theme-menu-head__subtitle">Personalizo pamjen e sistemit</p>
                </div>

                <div className="theme-menu-list">
                  {themeOptions.map((option) => {
                    const selected = themeMode === option.value;
                    const OptionIcon = option.icon;

                    return (
                      <MenuItem
                        key={option.value}
                        selected={selected}
                        onClick={() => handleSelectTheme(option.value)}
                        className={`theme-menu-item ${selected ? "is-active" : ""}`}
                      >
                        <span className={`theme-menu-dot theme-menu-dot--${option.value}`}></span>
                        <span className="theme-menu-item__icon-wrap">
                          <OptionIcon className="theme-menu-item__icon" />
                        </span>

                        <span className="theme-menu-item__copy">
                          <span className="theme-menu-item__label">{option.label}</span>
                          <span className="theme-menu-item__description">{option.description}</span>
                        </span>

                        {selected && (
                          <span className="theme-menu-item__status" aria-label="Tema aktive">
                            <Check className="theme-menu-item__check" />
                            Aktive
                          </span>
                        )}
                      </MenuItem>
                    );
                  })}
                </div>
              </div>
            </MenuMui>
          </>
        )}

        <div className="header-action-slot">
          <IconButton
            onClick={handleClickForms}
            className="icon-btn header-action-btn"
            aria-label="Hap hyrjet e shpejta"
            aria-haspopup="menu"
            aria-expanded={openForms}
          >
            <i className="bi bi-grid-3x3-gap-fill"></i>
          </IconButton>
        </div>

        <MenuMui
          anchorEl={forms}
          id="forms-menu"
          open={openForms}
          onClose={handleCloseForms}
          PaperProps={{
            elevation: 0,
            sx: {
              mt: 1,
              borderRadius: "16px",
              overflow: "hidden",
              border: "1px solid var(--header-dropdown-border)",
              boxShadow: "0 8px 30px rgba(15,23,42,0.14)",
              backgroundColor: "var(--header-dropdown-bg)",
              color: "var(--header-dropdown-text)",
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <div className="app-launcher">
            <div className="app-launcher__header">Hyrje e Shpejtë</div>
            <div className="app-launcher__grid">
              {[
                { to: "/Sales/Addsalesindetails", icon: "bi-cart-check-fill", label: "Shitje" },
                { to: "/purchases/addpurchaseinvoice", icon: "bi-bag-check-fill", label: "Blerje" },
                { to: "/expences/expensepurchases", icon: "bi-receipt-cutoff", label: "Shpenzime" },
                { to: "/articles/articleslist", icon: "bi-upc-scan", label: "Artikuj" },
                { to: "/accounts/clients", icon: "bi-person-plus-fill", label: "Klientë" },
                { to: "/accounts/suppliers", icon: "bi-person-dash-fill", label: "Furnizues" },
                { to: "/money/addreceivables", icon: "bi-cash-coin", label: "Arkëtime" },
                { to: "/money/addpayments", icon: "bi-wallet2", label: "Pagesa" },
                { to: "/money/addtransfers", icon: "bi-arrow-left-right", label: "Transferta" },
              ].map(({ to, icon, label }) => (
                <Link key={to} to={to} className="app-launcher__tile" onClick={handleCloseForms}>
                  <div className="app-launcher__tile-icon">
                    <i className={`bi ${icon}`}></i>
                  </div>
                  <span className="app-launcher__tile-label">{label}</span>
                </Link>
              ))}
            </div>
          </div>
        </MenuMui>

        <div className="header-action-slot">
          <IconButton
            onClick={handleNotifClick}
            className="icon-btn header-action-btn notification-btn"
            aria-label={`Njoftime${unreadNotificationCount ? `, ${unreadNotificationCount} të palexuara` : ""}`}
            aria-haspopup="dialog"
            aria-expanded={notifPopoverOpen}
          >
            <Badge badgeContent={unreadNotificationCount || null} color="error">
              <Bell className="icon-20" />
            </Badge>
          </IconButton>
        </div>

        <Popover
          open={notifPopoverOpen}
          anchorEl={notifAnchor}
          onClose={closeNotifPopover}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            className: "notification-popover-paper",
            sx: {
              width: 350,
              maxWidth: "calc(100vw - 24px)",
              backgroundColor: "var(--header-dropdown-bg)",
              color: "var(--header-dropdown-text)",
              border: "1px solid var(--header-dropdown-border)",
              borderRadius: "14px",
              boxShadow: "0 14px 34px rgba(0,0,0,0.24)",
              overflow: "hidden",
            },
          }}
        >
          <Box p={2} className="notification-popover-head">
            <Typography variant="subtitle1">Njoftime</Typography>
            <Typography variant="caption" className="notification-muted-text">
              {unreadNotificationCount} të palexuara
            </Typography>
          </Box>

          <List sx={{ maxHeight: 300, overflowY: "auto" }}>
            {userNotification.length === 0 ? (
              <Box p={1}>
                <Typography
                  variant="body2"
                  className="notification-muted-text"
                  align="center"
                >
                  Nuk ka asnjë njoftim
                </Typography>
              </Box>
            ) : (
              notifList.map((notification) => {
                const isRead = notification.IsRead ?? notification.isRead ?? false;
                return <div
                  key={notification.NotificationStatusID}
                  onClick={() =>
                    navigateToList(notification.NotificationStatusID)
                  }
                  className={`notification-entry ${isRead ? "is-read" : "is-unread"}`}
                >
                  <ListItem
                    button
                    onClick={() =>
                      setNotificationAsRead(notification.NotificationStatusID)
                    }
                    className="notification-list-item"
                    sx={{ alignItems: "flex-start" }}
                  >
                    <ListItemAvatar>
                      <Avatar className="notification-avatar" sx={{ fontSize: 12 }}>
                        {notification.Version || "!"}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle2"
                          fontWeight={isRead ? "normal" : "bold"}
                        >
                          {notification.Title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography
                            variant="body2"
                            className="notification-muted-text"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              cursor: "pointer",
                            }}
                          >
                            {notification.Message}
                          </Typography>
                          <Typography
                            variant="caption"
                            className="notification-muted-text"
                          >
                            {format(
                              new Date(notification.CreatedDate),
                              "MMM d, h:mm a"
                            )}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                </div>;
              })
            )}
          </List>

          <Box p={2} textAlign="center" className="notification-popover-footer">
            <Link to={"/configuration/notification"}>
              <Button
                size="small"
                onClick={closeNotifPopover}
                className="notification-view-all"
              >
                Shiko te gjitha njoftimet
              </Button>
            </Link>
          </Box>
        </Popover>

        <div className="profile-wrapper header-action-slot" ref={profileRef}>
          <button
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            className="profile-btn"
            aria-label="Hap menunë e profilit"
            aria-haspopup="menu"
            aria-expanded={isProfileOpen}
          >
            <div className="profile-text">
              <p className="name">
                {companyName || "Kompania"}
              </p>
              <p className="role">
                {email ? email.split("@acctree.net")[0] : "Përdoruesi"}
              </p>
            </div>

            <div className="avatar">
              <img
                src={getLogoSrc(logo)}
                alt="Logo e kompanisë"
                loading="lazy"
                className="profile-company-logo"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = ApiLink.PHOTO_URL + "companyLogo.png";
                }}
              />
            </div>
          </button>

          {isProfileOpen && (
            <div className="dropdown">
              <div className="dropdown-header">
                <p className="signed">I kyçur si</p>
                <p className="email">
                  {email || "user@example.com"}
                </p>
              </div>

              <div className="dropdown-section">
                <Link
                  to="/configuration/profile"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <button className="dropdown-item">
                    <UserIcon className="icon-16" /> Profili
                  </button>
                </Link>
                <Link
                  to="/configuration/users"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <button className="dropdown-item">
                    <Settings className="icon-16" /> Përdoruesit
                  </button>
                </Link>
              </div>

              <div className="dropdown-section border-top">
                <button
                  onClick={handleLogout}
                  className="dropdown-item logout"
                >
                  <LogOut className="icon-16" /> Dil
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
